import React, { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from './contexts/AuthContext'

// Layout (keep these eagerly loaded as they're used immediately)
import GlobalLayout from './components/GlobalLayout'
import ScrollToTop from './components/ScrollToTop'
import PWAInstall from './components/PWAInstall'

// Loading component for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
  </div>
)

// Critical pages (keep eagerly loaded)
import HomePage from './pages/HomePage'
import NotFound from './pages/NotFound'

// Lazy-loaded pages for better performance
// Service-related pages (grouped chunk: service-pages)
const FindContractors = React.lazy(() => import('./pages/FindContractors'))
const WaterDamageRestoration = React.lazy(() => import('./pages/WaterDamageRestoration'))
const FireDamageRestoration = React.lazy(() => import('./pages/FireDamageRestoration'))
const MoldRemediation = React.lazy(() => import('./pages/MoldRemediation'))
const WaterDamageSpecialists = React.lazy(() => import('./pages/WaterDamageSpecialists'))
const FireDamageSpecialists = React.lazy(() => import('./pages/FireDamageSpecialists'))

// Professional-related pages (grouped chunk: professional-pages)
const ForContractors = React.lazy(() => import('./pages/ForContractors'))
const RestorationProfessionals = React.lazy(() => import('./pages/RestorationProfessionals'))
const ProfessionalRequirements = React.lazy(() => import('./pages/ProfessionalRequirements'))
const ProfessionalPricing = React.lazy(() => import('./pages/ProfessionalPricing'))
const JoinNetwork = React.lazy(() => import('./pages/JoinNetwork'))

// Informational pages (grouped chunk: info-pages)
const AboutUs = React.lazy(() => import('./pages/AboutUs'))
const HowItWorks = React.lazy(() => import('./pages/HowItWorks'))
const HowItWorksHomeowners = React.lazy(() => import('./pages/HowItWorksHomeowners'))
const Resources = React.lazy(() => import('./pages/Resources'))
const Blog = React.lazy(() => import('./pages/Blog'))
const BurstPipePrevention = React.lazy(() => import('./pages/BurstPipePrevention'))
const FireDamageSafetyGuide = React.lazy(() => import('./pages/FireDamageSafetyGuide'))

// Auth pages (grouped chunk: auth-pages)
const Signup = React.lazy(() => import('./pages/Signup'))  // Contractor registration (Twilio compliant)
const SignupHomeowner = React.lazy(() => import('./pages/SignupHomeowner'))  // Homeowner registration
const Login = React.lazy(() => import('./pages/Login'))
const ProfessionalLogin = React.lazy(() => import('./pages/ContractorLogin'))

// Demo/Development pages
const GoldStandardDemo = React.lazy(() => import('./pages/GoldStandardDemo'))

// Legal and business pages (grouped chunk: other-pages)
const Privacy = React.lazy(() => import('./pages/Privacy'))
const Terms = React.lazy(() => import('./pages/Terms'))
const SMSDisclosure = React.lazy(() => import('./pages/SMSDisclosure'))
const Membership = React.lazy(() => import('./pages/Membership'))
const Contact = React.lazy(() => import('./pages/Contact'))

// Service request pages (critical for conversions, smaller chunk)
const RequestService = React.lazy(() => import('./pages/RequestService'))
const GetHelpNow = React.lazy(() => import('./pages/GetHelpNow'))

// Professional components (grouped chunk: professional-components)
const ContractorResults = React.lazy(() => import('./components/CleanContractorResults'))
const ProfessionalProfilePage = React.lazy(() => import('./components/ContractorProfilePage'))

// New Pages (lazy loaded)
const FAQ = React.lazy(() => import('./pages/FAQ'))
const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const LeadAction = React.lazy(() => import('./pages/LeadAction'))
const ServiceRequestSuccess = React.lazy(() => import('./pages/ServiceRequestSuccess'))
const Verification = React.lazy(() => import('./pages/Verification'))
const Sitemap = React.lazy(() => import('./pages/Sitemap'))
const InsuranceGuide = React.lazy(() => import('./pages/InsuranceGuide'))
const EmergencyChecklist = React.lazy(() => import('./pages/EmergencyChecklist'))
const Guides = React.lazy(() => import('./pages/Guides'))
const SmokeOdorRemoval = React.lazy(() => import('./pages/SmokeOdorRemoval'))
const InlandEmpireRestoration = React.lazy(() => import('./pages/InlandEmpireRestoration'))
const ApartmentCondoRestoration = React.lazy(() => import('./pages/ApartmentCondoRestoration'))
const ContractorDirectory = React.lazy(() => import('./pages/ContractorDirectory'))
const ProjectGallery = React.lazy(() => import('./pages/ProjectGallery'))
const Emergency = React.lazy(() => import('./pages/Emergency'))
const WhyFloodFirePros = React.lazy(() => import('./pages/WhyFloodFirePros'))

// Phase 3 Pages - Location, Property Type, and Service Pages
const StormDamageEmergency = React.lazy(() => import('./pages/StormDamageEmergency'))
const BeverlyHillsRestoration = React.lazy(() => import('./pages/BeverlyHillsRestoration'))
const LosAngelesRestoration = React.lazy(() => import('./pages/LosAngelesRestoration'))
const OrangeCountyRestoration = React.lazy(() => import('./pages/OrangeCountyRestoration'))
const SanDiegoRestoration = React.lazy(() => import('./pages/SanDiegoRestoration'))
const SacramentoRestoration = React.lazy(() => import('./pages/SacramentoRestoration'))
const FresnoRestoration = React.lazy(() => import('./pages/FresnoRestoration'))
const ResidentialRestoration = React.lazy(() => import('./pages/ResidentialRestoration'))
const CommercialRestoration = React.lazy(() => import('./pages/CommercialRestoration'))
const IndustrialRestoration = React.lazy(() => import('./pages/IndustrialRestoration'))

// Pack-Out Management (for contractors)
const PackoutDashboard = React.lazy(() => import('./pages/PackoutDashboard'))
const EmergencyGuide = React.lazy(() => import('./pages/EmergencyGuide'))
const EmergencyConfirmation = React.lazy(() => import('./pages/EmergencyConfirmation'))
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'))
const LocalBusinessListingsAdmin = React.lazy(() => import('./pages/LocalBusinessListingsAdmin'))
const PaidAdvertisingAdmin = React.lazy(() => import('./pages/PaidAdvertisingAdmin'))
const GoLiveMonitoringAdmin = React.lazy(() => import('./pages/GoLiveMonitoringAdmin'))
const StateLicensingAdmin = React.lazy(() => import('./pages/StateLicensingAdmin'))
const LiveDashboard = React.lazy(() => import('./pages/LiveDashboard'))
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'))
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'))
const VerifyPhone = React.lazy(() => import('./pages/VerifyPhone'))
const AdminRoute = React.lazy(() => import('./components/AdminRoute'))
import SmartMatchingWizardWrapper from './components/SmartMatchingWizardWrapper'
const DevHealthDashboard = React.lazy(() => import('./components/DevHealthDashboard'))
const EmergencyResponseChecklist = React.lazy(() => import('./pages/EmergencyResponseChecklist'))
const WaterDamagePreventionGuide = React.lazy(() => import('./pages/WaterDamagePreventionGuide'))

// Pack-Out Management System (Coming Soon - Backend not yet implemented)
// const PackoutApp = React.lazy(() => import('./pages/packout/PackoutApp'))

// Resource Pages (Tier 3 Member Resources)
const DashboardQuickStart = React.lazy(() => import('./pages/resources/DashboardQuickStart'))
const LeadManagementBestPractices = React.lazy(() => import('./pages/resources/LeadManagementBestPractices'))
const ProfileOptimizationChecklist = React.lazy(() => import('./pages/resources/ProfileOptimizationChecklist'))
const AnalyticsReportingTutorial = React.lazy(() => import('./pages/resources/AnalyticsReportingTutorial'))
const InstagramForContractors = React.lazy(() => import('./pages/resources/InstagramForContractors'))
const FacebookBusinessSetup = React.lazy(() => import('./pages/resources/FacebookBusinessSetup'))
const BeforeAfterPhotoStrategy = React.lazy(() => import('./pages/resources/BeforeAfterPhotoStrategy'))
const SocialMediaContentCalendar = React.lazy(() => import('./pages/resources/SocialMediaContentCalendar'))
const CustomerCommunicationTemplates = React.lazy(() => import('./pages/resources/CustomerCommunicationTemplates'))
const PricingStrategyWorksheet = React.lazy(() => import('./pages/resources/PricingStrategyWorksheet'))
const MarketingROICalculator = React.lazy(() => import('./pages/resources/MarketingROICalculator'))
const ReferralProgramBlueprint = React.lazy(() => import('./pages/resources/ReferralProgramBlueprint'))
const IICRCCertificationPathways = React.lazy(() => import('./pages/resources/IICRCCertificationPathways'))
const InsuranceClaimsMastery = React.lazy(() => import('./pages/resources/InsuranceClaimsMastery'))
const EmergencyResponseProtocols = React.lazy(() => import('./pages/resources/EmergencyResponseProtocols'))
const RegulatoryComplianceChecklist = React.lazy(() => import('./pages/resources/RegulatoryComplianceChecklist'))

// NEW Tier 3 Resource Pages (2025)
const DisasterRecoveryFundingOptions = React.lazy(() => import('./pages/resources/DisasterRecoveryFundingOptions'))
const EquipmentRentalRestorationTools = React.lazy(() => import('./pages/resources/EquipmentRentalRestorationTools'))
const RestorationCostEstimator = React.lazy(() => import('./pages/resources/RestorationCostEstimator'))
const FireSmokeHealthRisks = React.lazy(() => import('./pages/resources/FireSmokeHealthRisks'))
const WaterIntrusionMapping = React.lazy(() => import('./pages/resources/WaterIntrusionMapping'))
const InvoicePaymentPolicyGuide = React.lazy(() => import('./pages/resources/InvoicePaymentPolicyGuide'))
const LocalDisasterRecoveryMap = React.lazy(() => import('./pages/resources/LocalDisasterRecoveryMap'))
const EmergencyDispatchSimulation = React.lazy(() => import('./pages/resources/EmergencyDispatchSimulation'))
const ContractorTerritoryLookup = React.lazy(() => import('./pages/resources/ContractorTerritoryLookup'))
const RestorationIndustryStandards = React.lazy(() => import('./pages/resources/RestorationIndustryStandards'))
const PlatformIntegrations = React.lazy(() => import('./pages/resources/PlatformIntegrations'))
const DisasterPsychologyFamilyRecovery = React.lazy(() => import('./pages/resources/DisasterPsychologyFamilyRecovery'))
const FireSafeLandscapingDefensibleSpace = React.lazy(() => import('./pages/resources/FireSafeLandscapingDefensibleSpace'))
const PreventativeMaintenanceCalendar = React.lazy(() => import('./pages/resources/PreventativeMaintenanceCalendar'))
const InsuranceCompanyDirectory = React.lazy(() => import('./pages/resources/InsuranceCompanyDirectory'))
const ProfessionalDevelopmentTraining = React.lazy(() => import('./pages/resources/ProfessionalDevelopmentTraining'))
const ContractorVerificationLookup = React.lazy(() => import('./pages/resources/ContractorVerificationLookup'))
const RealCustomerStories = React.lazy(() => import('./pages/resources/RealCustomerStories'))
const PerformanceMetricsAnalytics = React.lazy(() => import('./pages/resources/PerformanceMetricsAnalytics'))
const EmergencyPlanningProtocols = React.lazy(() => import('./pages/resources/EmergencyPlanningProtocols'))
const CommunityEngagementOutreach = React.lazy(() => import('./pages/resources/CommunityEngagementOutreach'))
const KnowledgeBase = React.lazy(() => import('./pages/resources/KnowledgeBase'))
const DisasterAlertHub = React.lazy(() => import('./pages/resources/DisasterAlertHub'))
const TechnologyAdoptionRoadmap = React.lazy(() => import('./pages/resources/TechnologyAdoptionRoadmap'))
const RestorationAftercareGuide = React.lazy(() => import('./pages/resources/RestorationAftercareGuide'))
const InnovationTechnologyRestoration = React.lazy(() => import('./pages/resources/InnovationTechnologyRestoration'))
const VendorSupplierRelationships = React.lazy(() => import('./pages/resources/VendorSupplierRelationships'))
const StructuralDryingScience = React.lazy(() => import('./pages/resources/StructuralDryingScience'))
const MoldHealthLegalLiability = React.lazy(() => import('./pages/resources/MoldHealthLegalLiability'))
const PhotoDocumentationBestPractices = React.lazy(() => import('./pages/resources/PhotoDocumentationBestPractices'))
const ClaimDisputeResolution = React.lazy(() => import('./pages/resources/ClaimDisputeResolution'))
const RestorationProjectTimeline = React.lazy(() => import('./pages/resources/RestorationProjectTimeline'))
const RebuildReconstructionOptions = React.lazy(() => import('./pages/resources/RebuildReconstructionOptions'))
const PlumbingFailurePreventionGuide = React.lazy(() => import('./pages/resources/PlumbingFailurePreventionGuide'))
const AirQualityDeodorizationTechniques = React.lazy(() => import('./pages/resources/AirQualityDeodorizationTechniques'))
const HomeownerEducationCenter = React.lazy(() => import('./pages/resources/HomeownerEducationCenter'))
const PreferredVendorCertification = React.lazy(() => import('./pages/resources/PreferredVendorCertification'))
const ContractorToolbelt = React.lazy(() => import('./pages/resources/ContractorToolbelt'))

// Phase 3 Pages (P1.7 - P1.11) - Insurance, Glossary, Reviews
const InsuranceEducation = React.lazy(() => import('./pages/InsuranceEducation'))
const StateFarmApproved = React.lazy(() => import('./pages/StateFarmApproved'))
const InsuranceClaimWizard = React.lazy(() => import('./pages/InsuranceClaimWizard'))
const Glossary = React.lazy(() => import('./pages/Glossary'))
const CustomerReviews = React.lazy(() => import('./pages/CustomerReviews'))

// Main App Component with Navigation
function AppContent() {
  const navigate = useNavigate()

  const handleLaunchWizard = () => {
    navigate('/smart-matching-wizard')
  }

  return (
    <GlobalLayout onLaunchWizard={handleLaunchWizard} containerized={false}>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
            {/* Homepage */}
            <Route path="/" element={<HomePage />} />
            <Route path="/get-help-now" element={<GetHelpNow />} />

            {/* Service Request */}
            <Route path="/request-service" element={<RequestService />} />

            {/* Service Pages */}
            <Route path="/water-damage-restoration" element={<WaterDamageRestoration />} />
            <Route path="/water-damage-restoration/" element={<WaterDamageRestoration />} />
            <Route path="/fire-damage-restoration" element={<FireDamageRestoration />} />
            <Route path="/fire-damage-restoration/" element={<FireDamageRestoration />} />
            <Route path="/mold-remediation" element={<MoldRemediation />} />
            <Route path="/mold-damage-restoration" element={<MoldRemediation />} />
            <Route path="/mold-damage-restoration/" element={<MoldRemediation />} />
            <Route path="/smoke-odor-removal" element={<SmokeOdorRemoval />} />
            <Route path="/smoke-odor-removal/" element={<SmokeOdorRemoval />} />

            {/* Directory & Search - Consolidated to use CleanContractorResults only */}
            <Route path="/find-contractors" element={<FindContractors />} />
            <Route path="/find-contractors/" element={<FindContractors />} />
            <Route path="/contractor-directory" element={<ContractorDirectory />} />
            <Route path="/contractor-directory/" element={<ContractorDirectory />} />
            <Route path="/gallery" element={<ProjectGallery />} />
            <Route path="/gallery/" element={<ProjectGallery />} />
            <Route path="/emergency" element={<Emergency />} />
            <Route path="/emergency/" element={<Emergency />} />
            <Route path="/why-flood-fire-pros" element={<WhyFloodFirePros />} />
            <Route path="/why-flood-fire-pros/" element={<WhyFloodFirePros />} />
            <Route path="/results/:zip" element={<ContractorResults />} />
            <Route path="/clean-results/:zip" element={<ContractorResults />} />
            <Route path="/contractor/:contractorId" element={<ProfessionalProfilePage />} />
            <Route path="/professionals/:id" element={<ProfessionalProfilePage />} />

            {/* Business Pages */}
            <Route path="/for-professionals" element={<ForContractors />} />
            <Route path="/for-contractors" element={<ForContractors />} />
            <Route path="/contractors" element={<ForContractors />} />
            <Route path="/restoration-professionals" element={<RestorationProfessionals />} />
            <Route path="/restoration-professionals/" element={<RestorationProfessionals />} />
            <Route path="/join-network" element={<JoinNetwork />} />
            <Route path="/join-network/" element={<JoinNetwork />} />

            {/* Tier 3 Professional Pages */}
            <Route path="/professional-requirements" element={<ProfessionalRequirements />} />
            <Route path="/professional-pricing" element={<ProfessionalPricing />} />
            <Route path="/water-damage-specialists" element={<WaterDamageSpecialists />} />
            <Route path="/fire-damage-specialists" element={<FireDamageSpecialists />} />

            {/* Information Pages */}
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/" element={<Resources />} />

            {/* Resource Pages (Member Resources - Tier 3) */}
            <Route path="/resources/dashboard-quick-start" element={<DashboardQuickStart />} />
            <Route path="/resources/lead-management-best-practices" element={<LeadManagementBestPractices />} />
            <Route path="/resources/profile-optimization-checklist" element={<ProfileOptimizationChecklist />} />
            <Route path="/resources/analytics-reporting-tutorial" element={<AnalyticsReportingTutorial />} />
            <Route path="/resources/instagram-for-contractors" element={<InstagramForContractors />} />
            <Route path="/resources/facebook-business-setup" element={<FacebookBusinessSetup />} />
            <Route path="/resources/before-after-photo-strategy" element={<BeforeAfterPhotoStrategy />} />
            <Route path="/resources/social-media-content-calendar" element={<SocialMediaContentCalendar />} />
            <Route path="/resources/customer-communication-templates" element={<CustomerCommunicationTemplates />} />
            <Route path="/resources/pricing-strategy-worksheet" element={<PricingStrategyWorksheet />} />
            <Route path="/resources/marketing-roi-calculator" element={<MarketingROICalculator />} />
            <Route path="/resources/referral-program-blueprint" element={<ReferralProgramBlueprint />} />
            <Route path="/resources/iicrc-certification-pathways" element={<IICRCCertificationPathways />} />
            <Route path="/resources/insurance-claims-mastery" element={<InsuranceClaimsMastery />} />
            <Route path="/resources/emergency-response-protocols" element={<EmergencyResponseProtocols />} />
            <Route path="/resources/regulatory-compliance-checklist" element={<RegulatoryComplianceChecklist />} />

            {/* NEW Tier 3 Resource Pages (2025) */}
            <Route path="/resources/disaster-recovery-funding-options" element={<DisasterRecoveryFundingOptions />} />
            <Route path="/resources/equipment-rental-tools" element={<EquipmentRentalRestorationTools />} />
            <Route path="/resources/restoration-cost-estimator" element={<RestorationCostEstimator />} />
            <Route path="/resources/fire-smoke-health-risks" element={<FireSmokeHealthRisks />} />
            <Route path="/resources/water-intrusion-mapping" element={<WaterIntrusionMapping />} />
            <Route path="/resources/invoice-payment-policy-guide" element={<InvoicePaymentPolicyGuide />} />
            <Route path="/resources/local-disaster-recovery-map" element={<LocalDisasterRecoveryMap />} />
            <Route path="/resources/emergency-dispatch-simulation" element={<EmergencyDispatchSimulation />} />
            <Route path="/resources/contractor-territory-lookup" element={<ContractorTerritoryLookup />} />
            <Route path="/resources/restoration-industry-standards" element={<RestorationIndustryStandards />} />
            <Route path="/resources/platform-integrations" element={<PlatformIntegrations />} />
            <Route path="/resources/disaster-psychology-family-recovery" element={<DisasterPsychologyFamilyRecovery />} />
            <Route path="/resources/fire-safe-landscaping-defensible-space" element={<FireSafeLandscapingDefensibleSpace />} />
            <Route path="/resources/preventative-maintenance-calendar" element={<PreventativeMaintenanceCalendar />} />
            <Route path="/resources/insurance-company-directory" element={<InsuranceCompanyDirectory />} />
            <Route path="/resources/professional-development-training" element={<ProfessionalDevelopmentTraining />} />
            <Route path="/resources/contractor-verification-lookup" element={<ContractorVerificationLookup />} />
            <Route path="/resources/real-customer-stories" element={<RealCustomerStories />} />
            <Route path="/resources/performance-metrics-analytics" element={<PerformanceMetricsAnalytics />} />
            <Route path="/resources/emergency-planning-protocols" element={<EmergencyPlanningProtocols />} />
            <Route path="/resources/community-engagement-outreach" element={<CommunityEngagementOutreach />} />
            <Route path="/resources/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/resources/disaster-alert-hub" element={<DisasterAlertHub />} />
            <Route path="/resources/technology-adoption-roadmap" element={<TechnologyAdoptionRoadmap />} />
            <Route path="/resources/restoration-aftercare-guide" element={<RestorationAftercareGuide />} />
            <Route path="/resources/innovation-technology-restoration" element={<InnovationTechnologyRestoration />} />
            <Route path="/resources/vendor-supplier-relationships" element={<VendorSupplierRelationships />} />
            <Route path="/resources/structural-drying-science" element={<StructuralDryingScience />} />
            <Route path="/resources/mold-health-legal-liability" element={<MoldHealthLegalLiability />} />
            <Route path="/resources/photo-documentation-best-practices" element={<PhotoDocumentationBestPractices />}
            />
            <Route path="/resources/claim-dispute-resolution" element={<ClaimDisputeResolution />} />
            <Route path="/resources/restoration-project-timeline" element={<RestorationProjectTimeline />} />
            <Route path="/resources/rebuild-reconstruction-options" element={<RebuildReconstructionOptions />} />
            <Route path="/resources/plumbing-failure-prevention-guide" element={<PlumbingFailurePreventionGuide />} />
            <Route path="/resources/air-quality-deodorization-techniques" element={<AirQualityDeodorizationTechniques/>}
            />
            <Route path="/resources/homeowner-education-center" element={<HomeownerEducationCenter />} />
            <Route path="/resources/preferred-vendor-certification" element={<PreferredVendorCertification />} />
            <Route path="/resources/contractor-toolbelt" element={<ContractorToolbelt />} />
            <Route path="/resources/disaster-recovery-funding-options" element={<DisasterRecoveryFundingOptions />} />

            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/how-it-works-homeowners" element={<HowItWorksHomeowners />} />
            <Route path="/how-it-works-homeowners/" element={<HowItWorksHomeowners />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/insurance-guide" element={<InsuranceGuide />} />
            <Route path="/emergency-checklist" element={<EmergencyChecklist />} />
            <Route path="/emergency-guide" element={<EmergencyGuide />} />
            <Route path="/emergency-confirmation" element={<EmergencyConfirmation />} />

            {/* PDF Guide Pages with Download Functionality */}
            <Route path="/emergency-response-checklist" element={<EmergencyResponseChecklist />} />
            <Route path="/water-damage-prevention-guide" element={<WaterDamagePreventionGuide />} />
            <Route path="/water-damage-emergency-checklist" element={<EmergencyResponseChecklist />} />
            <Route path="/fire-damage-safety-guide" element={<FireDamageSafetyGuide />} />

            {/* Tier 3 Specialized Pages */}
            <Route path="/burst-pipe-prevention" element={<BurstPipePrevention />} />

            {/* Blog Post Pages */}
            <Route path="/blog/emergency-water-damage-restoration-signs" element={<WaterDamageRestoration />} />
            <Route path="/blog/fire-damage-insurance-claims-guide" element={<FireDamageRestoration />} />
            <Route path="/blog/mold-health-property-impact-guide" element={<MoldRemediation />} />
            <Route path="/blog/spring-flood-preparedness-california" element={<WaterDamageRestoration />} />
            <Route path="/blog/choosing-iicrc-certified-contractors" element={<ForContractors />} />
            <Route path="/blog/smoke-damage-invisible-threat-after-fire" element={<FireDamageRestoration />} />

            {/* Location Pages */}
            <Route path="/inland-empire-restoration" element={<InlandEmpireRestoration />} />
            <Route path="/inland-empire-restoration/" element={<InlandEmpireRestoration />} />
            <Route path="/beverly-hills-restoration" element={<BeverlyHillsRestoration />} />
            <Route path="/beverly-hills-restoration/" element={<BeverlyHillsRestoration />} />
            <Route path="/los-angeles-restoration" element={<LosAngelesRestoration />} />
            <Route path="/los-angeles-restoration/" element={<LosAngelesRestoration />} />
            <Route path="/orange-county-restoration" element={<OrangeCountyRestoration />} />
            <Route path="/orange-county-restoration/" element={<OrangeCountyRestoration />} />
            <Route path="/san-diego-restoration" element={<SanDiegoRestoration />} />
            <Route path="/san-diego-restoration/" element={<SanDiegoRestoration />} />
            <Route path="/sacramento-restoration" element={<SacramentoRestoration />} />
            <Route path="/sacramento-restoration/" element={<SacramentoRestoration />} />
            <Route path="/fresno-restoration" element={<FresnoRestoration />} />
            <Route path="/fresno-restoration/" element={<FresnoRestoration />} />

            {/* Property Type Pages */}
            <Route path="/apartment-condo-restoration" element={<ApartmentCondoRestoration />} />
            <Route path="/apartment-condo-restoration/" element={<ApartmentCondoRestoration />} />
            <Route path="/residential-restoration" element={<ResidentialRestoration />} />
            <Route path="/residential-restoration/" element={<ResidentialRestoration />} />
            <Route path="/commercial-restoration" element={<CommercialRestoration />} />
            <Route path="/commercial-restoration/" element={<CommercialRestoration />} />
            <Route path="/industrial-restoration" element={<IndustrialRestoration />} />
            <Route path="/industrial-restoration/" element={<IndustrialRestoration />} />

            {/* Emergency Service Pages */}
            <Route path="/storm-damage-emergency" element={<StormDamageEmergency />} />
            <Route path="/storm-damage-emergency/" element={<StormDamageEmergency />} />

            {/* Auth Pages */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-homeowner" element={<SignupHomeowner />} />
            <Route path="/contractor-signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/professional-login" element={<ProfessionalLogin />} />
            <Route path="/contractor-login" element={<ProfessionalLogin />} />
            <Route path="/professional-dashboard" element={<Dashboard />} />
            <Route path="/contractor-dashboard" element={<Dashboard />} />

            {/* Company Pages */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />

            {/* Core Pages */}
            <Route path="/membership" element={<Membership />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Lead Action (Contractor SMS link) */}
            <Route path="/lead/:leadId" element={<LeadAction />} />
              <Route path="/request-success" element={<ServiceRequestSuccess />} />
            <Route path="/packout" element={<PackoutDashboard />} />
            <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/local-listings" element={<AdminRoute><LocalBusinessListingsAdmin /></AdminRoute>} />
            <Route path="/admin/paid-advertising" element={<AdminRoute><PaidAdvertisingAdmin /></AdminRoute>} />
            <Route path="/admin/go-live-monitoring" element={<AdminRoute><GoLiveMonitoringAdmin /></AdminRoute>} />
            <Route path="/admin/state-licensing" element={<AdminRoute><StateLicensingAdmin /></AdminRoute>} />
            <Route path="/admin/live-dashboard" element={<AdminRoute><LiveDashboard /></AdminRoute>} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/verify-phone" element={<VerifyPhone />} />
            <Route path="/smart-matching-wizard" element={<SmartMatchingWizardWrapper />} />
            <Route path="/dev-health" element={<DevHealthDashboard />} />
            <Route path="/gold-standard-demo" element={<GoldStandardDemo />} />
            <Route path="/verification" element={<Verification />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-disclosure" element={<SMSDisclosure />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Phase 3 Pages (P1.7 - P1.11) - Insurance, Glossary, Reviews */}
            <Route path="/insurance-education" element={<InsuranceEducation />} />
            <Route path="/insurance-education/" element={<InsuranceEducation />} />
            <Route path="/state-farm-approved" element={<StateFarmApproved />} />
            <Route path="/state-farm-approved/" element={<StateFarmApproved />} />
            <Route path="/insurance-claim-wizard" element={<InsuranceClaimWizard />} />
            <Route path="/insurance-claim-wizard/" element={<InsuranceClaimWizard />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/glossary/" element={<Glossary />} />
            <Route path="/customer-reviews" element={<CustomerReviews />} />
            <Route path="/customer-reviews/" element={<CustomerReviews />} />

            {/* Test Pages */}
            <Route path="/test" element={<div className="p-8 text-center"><h1 className="text-2xl font-bold">Test Page - Header and Footer should be visible</h1></div>} />

            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </GlobalLayout>
  )
}

// PWA Service Worker Registration
const registerServiceWorker = async () => {
  // Skip service worker registration in development to prevent reload loops
  if (import.meta.env.MODE === 'development') {
    return;
  }

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
            // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
            }
          });
        }
      });
    } catch (error) {
    }
  }
};

// Wrapper App component
function App() {
  useEffect(() => {
    // Register service worker
    registerServiceWorker();

    // Track app load for analytics
    if (window.gtag) {
      window.gtag('event', 'app_load', {
        event_category: 'PWA',
        event_label: navigator.standalone ? 'Standalone' : 'Browser'
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppContent />
          <PWAInstall />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  )
}

export default App
